import { useDialog, useMessage, useNotification } from 'naive-ui'
import { ApmpApiError } from '~/shared/api/errors'

/**
 * The single feedback surface. Pages never import useMessage/useDialog/useNotification
 * directly, so tone and duration stay consistent and can be changed in one place.
 */
export function useFeedback() {
  const message = useMessage()
  const notification = useNotification()
  const dialog = useDialog()

  function success(text: string) {
    message.success(text)
  }

  function info(text: string) {
    message.info(text)
  }

  /**
   * Renders a failure honestly: the server's message, plus the correlation id when there is
   * one, because that is the only thing support can search for.
   */
  function error(cause: unknown, fallback = 'Something went wrong. Please try again.') {
    if (cause instanceof ApmpApiError) {
      if (cause.status >= 500 && cause.correlationId) {
        notification.error({
          title: cause.message,
          content: `Reference: ${cause.correlationId}`,
          duration: 8000,
        })
        return
      }
      message.error(cause.message)
      return
    }
    message.error(fallback)
  }

  function confirm(options: {
    title: string
    content: string
    positiveText?: string
    negativeText?: string
    danger?: boolean
  }): Promise<boolean> {
    return new Promise((resolve) => {
      dialog[options.danger ? 'warning' : 'info']({
        title: options.title,
        content: options.content,
        positiveText: options.positiveText ?? 'Confirm',
        negativeText: options.negativeText ?? 'Cancel',
        onPositiveClick: () => resolve(true),
        onNegativeClick: () => resolve(false),
        onClose: () => resolve(false),
        onMaskClick: () => resolve(false),
      })
    })
  }

  return { success, info, error, confirm }
}
